/**
 * <bundle-upsell> — pair-product upsell card below the buy buttons.
 *
 * Follows the main product's metal option (option 1 / Farbe): on
 * PUB_SUB_EVENTS.variantChange it picks the pair variant with the same
 * option1 value (index fallback) and updates image, price, savings badge,
 * match line and the variant id used for add-to-cart.
 *
 * Single-option pair products add directly. Multi-option pair products
 * (e.g. necklace upsell on earring PDPs) open a picker for the remaining
 * options — a centered dialog on desktop, a bottom sheet on mobile. The
 * modal markup is rendered by bundle-upsell.liquid and moved to <body>
 * so sticky/transformed ancestors can't trap the fixed overlay.
 *
 * Adds ONLY the pair product to the cart via window.CartAdd — the main
 * product form is untouched.
 */
if (!customElements.get('bundle-upsell')) {
  customElements.define(
    'bundle-upsell',
    class BundleUpsell extends HTMLElement {
      constructor() {
        super()
        this.onAddClick = this.onAddClick.bind(this)
        this.onConfirmClick = this.onConfirmClick.bind(this)
        this.onModalChange = this.onModalChange.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.closeModal = this.closeModal.bind(this)
      }

      connectedCallback() {
        try {
          this.variants = JSON.parse(this.dataset.variants || '[]')
        } catch (e) {
          this.variants = []
        }
        this.mainOptions = (this.dataset.mainOptions || '').split('||').filter(Boolean)
        this.pairOptions = (this.dataset.pairOptions || '').split('||').filter(Boolean)
        this.pairOptionNames = (this.dataset.pairOptionNames || '').split('||').filter(Boolean)
        this.multiOption = parseInt(this.dataset.optionCount || '1', 10) > 1

        // Swatch backgrounds from the native option-value swatch drops (same source as the variant picker)
        this.swatches = {}
        try {
          const raw = JSON.parse(this.dataset.swatches || '{}')
          Object.keys(raw).forEach((key) => {
            this.swatches[this.normalize(key)] = raw[key]
          })
        } catch (e) {
          /* ignore */
        }

        // Collect refs before the modal is moved to <body>; dots/match lines exist in card AND modal.
        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0
        this.mainCompare = parseInt(this.dataset.mainCompare, 10) || 0

        this.button = this.querySelector('[data-bundle-atc]')
        this.buttonText = this.querySelector('[data-bundle-atc-text]')
        this.imageEl = this.querySelector('[data-bundle-image]')
        this.mainImageEl = this.querySelector('[data-bundle-main-image]')
        this.priceEl = this.querySelector('[data-bundle-price]')
        this.compareEl = this.querySelector('[data-bundle-compare]')
        this.saveEl = this.querySelector('[data-bundle-save]')
        this.matchEls = Array.from(this.querySelectorAll('[data-bundle-match]'))
        this.dotEls = Array.from(this.querySelectorAll('[data-bundle-dot]'))
        this.errorEl = this.querySelector('[data-bundle-error]')

        this.modal = this.querySelector('[data-bundle-modal]')
        if (this.modal) {
          this.modalImage = this.modal.querySelector('[data-bundle-modal-image]')
          this.gallery = this.modal.querySelector('[data-bundle-modal-gallery]')
          const prev = this.modal.querySelector('[data-bundle-gallery-prev]')
          const next = this.modal.querySelector('[data-bundle-gallery-next]')
          if (this.gallery && prev && next) {
            const step = (dir) => {
              // Measure a VISIBLE slide — hidden (filtered-out) slides have zero width
              const slide = Array.from(this.gallery.querySelectorAll('.bundle-upsell-modal__slide')).find(
                (s) => s.style.display !== 'none'
              )
              const width = slide ? slide.getBoundingClientRect().width : this.gallery.clientWidth
              this.gallery.scrollBy({ left: dir * width, behavior: 'smooth' })
            }
            prev.addEventListener('click', () => step(-1))
            next.addEventListener('click', () => step(1))
          }
          this.modalPrice = this.modal.querySelector('[data-bundle-modal-price]')
          this.modalCompare = this.modal.querySelector('[data-bundle-modal-compare]')
          this.confirmBtn = this.modal.querySelector('[data-bundle-modal-confirm]')
          this.confirmText = this.modal.querySelector('[data-bundle-modal-confirm-text]')
          this.optionFieldsets = Array.from(this.modal.querySelectorAll('[data-bundle-modal-option]'))
          this.modalMatchEl = this.modal.querySelector('[data-bundle-match]')
          this.modal.querySelectorAll('[data-bundle-modal-close]').forEach((el) => {
            el.addEventListener('click', this.closeModal)
          })
          this.modal.addEventListener('change', this.onModalChange)
          if (this.confirmBtn) this.confirmBtn.addEventListener('click', this.onConfirmClick)
          document.body.appendChild(this.modal)
        }

        if (this.button) this.button.addEventListener('click', this.onAddClick)

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event.data || event.data.sectionId !== this.dataset.section) return
          this.syncToMainVariant(event.data.variant)
        })

        this.currentOption1 = this.dataset.initialOption1 || ''
        if (this.currentOption1) {
          this.syncToMainVariant({ option1: this.currentOption1 })
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber()
        if (this.button) this.button.removeEventListener('click', this.onAddClick)
        document.removeEventListener('keydown', this.onKeyDown)
        if (this.modal && this.modal.parentElement === document.body) {
          document.body.removeChild(this.modal)
        }
      }

      normalize(s) {
        if (s == null) return ''
        return String(s).replace(/\s+/g, ' ').trim().toLowerCase()
      }

      /** option1 value translated to the pair product's naming (index fallback for renamed values). */
      pairOption1Candidates(option1) {
        const name = this.normalize(option1)
        const candidates = [name]
        const idx = this.mainOptions.findIndex((o) => this.normalize(o) === name)
        if (idx >= 0 && this.pairOptions[idx]) {
          const pairName = this.normalize(this.pairOptions[idx])
          if (pairName && pairName !== name) candidates.push(pairName)
        }
        return candidates
      }

      /** Pair variant whose option1 matches the main variant's option1 (available first). */
      findMatch(mainVariant) {
        if (!mainVariant || mainVariant.option1 == null) return null
        for (const candidate of this.pairOption1Candidates(mainVariant.option1)) {
          const match =
            this.variants.find((v) => this.normalize(v.o1) === candidate && v.a) ||
            this.variants.find((v) => this.normalize(v.o1) === candidate)
          if (match) return match
        }
        return null
      }

      /** "Farbe: Gold / Kette: …" — option names and values come server-rendered (translated), same source as the variant selector. */
      pairVariantTitle(v) {
        return [v.o1, v.o2, v.o3]
          .filter(Boolean)
          .map((val, i) => (this.pairOptionNames[i] ? `${this.pairOptionNames[i]}: ${val}` : val))
          .join(' / ')
      }

      updateDot(optionName) {
        const swatch = this.swatches[this.normalize(optionName)]
        this.dotEls.forEach((dot) => {
          if (swatch) dot.style.setProperty('--swatch--background', swatch)
          else dot.style.removeProperty('--swatch--background')
        })
      }

      /** Set total: main variant + given pair variant (compare-at falls back to price per item). */
      setTotal(pairPrice, pairCompare) {
        const total = this.mainPrice + pairPrice
        const compareTotal =
          Math.max(this.mainCompare || 0, this.mainPrice) + Math.max(pairCompare || 0, pairPrice)
        return { total, compareTotal, savings: compareTotal - total }
      }

      syncToMainVariant(mainVariant) {
        this.hideError()
        const match = this.findMatch(mainVariant)
        const optionName = mainVariant && mainVariant.option1 != null ? String(mainVariant.option1) : ''
        if (optionName) this.currentOption1 = optionName

        if (mainVariant && typeof mainVariant.price === 'number') {
          this.mainPrice = mainVariant.price
          this.mainCompare = mainVariant.compare_at_price || 0
        }
        if (this.mainImageEl && mainVariant && mainVariant.featured_image && mainVariant.featured_image.src) {
          this.mainImageEl.src = mainVariant.featured_image.src
        }

        if (this.matchEls.length && match) {
          const text = this.pairVariantTitle(match)
          this.matchEls.forEach((el) => (el.textContent = text))
        }
        this.updateDot(optionName)

        if (!match) {
          this.dataset.variantId = ''
          this.setSoldOut(true)
          return
        }

        this.dataset.variantId = String(match.id)
        if (this.imageEl && match.i) this.imageEl.src = match.i

        const { total, compareTotal, savings } = this.setTotal(match.p, match.cap)
        if (this.priceEl) this.priceEl.textContent = this.formatMoney(total)
        if (this.compareEl) {
          this.compareEl.hidden = savings === 0
          if (savings > 0) this.compareEl.textContent = this.formatMoney(compareTotal)
        }
        if (this.saveEl && this.saveEl.hasAttribute('data-badge-auto')) {
          this.saveEl.hidden = savings === 0
          if (savings > 0) {
            this.saveEl.textContent = (this.dataset.saveTemplate || '[amount]').replace(
              '[amount]',
              this.formatMoney(savings)
            )
          }
        }

        this.setSoldOut(!match.a)
        if (this.modal && this.modal.classList.contains('is-open')) this.updateModalState()
      }

      setSoldOut(soldOut) {
        this.classList.toggle('bundle-upsell--soldout', soldOut)
        if (this.button) {
          if (soldOut) this.button.setAttribute('disabled', 'disabled')
          else this.button.removeAttribute('disabled')
        }
        if (this.buttonText) {
          this.buttonText.textContent = soldOut
            ? this.dataset.soldOutLabel || ''
            : this.dataset.buttonLabel || ''
        }
      }

      formatMoney(cents) {
        if (cents == null || isNaN(cents)) return ''
        const amount = (cents / 100).toFixed(2)
        const amountComma = amount.replace('.', ',')
        const format = this.dataset.moneyFormat || '{{ amount }}'
        return format
          .replace(/\{\{\s*amount\s*\}\}/gi, amount)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/gi, amountComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, Math.round(cents / 100).toString())
          .trim()
      }

      showError(message) {
        if (!this.errorEl || !message) return
        this.errorEl.textContent = message
        this.errorEl.hidden = false
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true
      }

      /* ========== Modal (multi-option pair products) ========== */

      getSelections() {
        const selections = {}
        this.optionFieldsets.forEach((fieldset) => {
          const checked = fieldset.querySelector('input:checked')
          selections[fieldset.dataset.optionPosition] = checked ? checked.value : null
        })
        return selections
      }

      variantForSelections(selections) {
        for (const candidate of this.pairOption1Candidates(this.currentOption1)) {
          const match = this.variants.find((v) => {
            if (this.normalize(v.o1) !== candidate) return false
            if (selections['2'] != null && this.normalize(v.o2) !== this.normalize(selections['2'])) return false
            if (selections['3'] != null && this.normalize(v.o3) !== this.normalize(selections['3'])) return false
            return true
          })
          if (match) return match
        }
        return null
      }

      openModal() {
        if (!this.modal) return
        // Default to a selection that is actually available for the current metal.
        const selections = this.getSelections()
        const current = this.variantForSelections(selections)
        if (!current || !current.a) {
          const fieldset = this.optionFieldsets[0]
          if (fieldset) {
            for (const radio of fieldset.querySelectorAll('input')) {
              const test = { ...selections, [fieldset.dataset.optionPosition]: radio.value }
              const v = this.variantForSelections(test)
              if (v && v.a) {
                radio.checked = true
                break
              }
            }
          }
        }
        this.updateModalState()
        this.modal.classList.add('is-open')
        this.modal.setAttribute('aria-hidden', 'false')
        document.body.classList.add('overflow-hidden')
        document.addEventListener('keydown', this.onKeyDown)
        this.scrollGalleryToVariant(this.variantForSelections(this.getSelections()), true)
        const dialog = this.modal.querySelector('.bundle-upsell-modal__dialog')
        if (dialog) dialog.focus()
      }

      closeModal() {
        if (!this.modal) return
        this.modal.classList.remove('is-open')
        this.modal.setAttribute('aria-hidden', 'true')
        document.body.classList.remove('overflow-hidden')
        document.removeEventListener('keydown', this.onKeyDown)
      }

      onKeyDown(evt) {
        if (evt.key === 'Escape') this.closeModal()
      }

      /* Same alt-token convention as the PDP gallery (media-gallery.js parseAltConstraints/variantCheck):
         alt like "farbe:gold:kette:…" — value tokens must match the active variant's options;
         comma-separated values ("Kette:585, Kette:333 GG") are alternatives for that option;
         untagged media always shows, "x"/"exclude" always hides. */
      parseAltConstraints(alt) {
        const byName = new Map()
        String(alt || '')
          .split(/\s*,\s*(?=[^,:]+:)/)
          .forEach((chunk) => {
            const parts = chunk.split(':')
            for (let i = 0; i + 1 < parts.length; i += 2) {
              const name = parts[i].trim().toLowerCase()
              const values = parts[i + 1]
                .split(',')
                .map((value) => value.trim().toLowerCase())
                .filter((value) => value && value !== 'secondary')
              if (!name || !values.length) continue
              byName.set(name, (byName.get(name) || []).concat(values))
            }
          })
        return byName
      }

      variantAltTokens(alt) {
        const tokens = []
        this.parseAltConstraints(alt).forEach((values) => {
          values.forEach((value) => tokens.push(value))
        })
        return tokens
      }

      optionValueMatches(options, token) {
        return options.some((option) => {
          const value = String(option || '').trim().toLowerCase()
          return value === token
        })
      }

      variantCheck(options, alt) {
        if (!alt || !alt.includes(':') || alt.split(':')[1] === '') return true
        const flags = alt
          .toLowerCase()
          .split(/[:,]/)
          .map((part) => part.trim())
        if (flags.includes('x') || flags.includes('exclude') || flags.includes('excluded')) return false
        const known = this.allPairOptionValues
        const selected = (options || []).map((option) => String(option || '').trim().toLowerCase())
        for (const values of this.parseAltConstraints(alt).values()) {
          const required = values.filter((part) =>
            known && known.size ? known.has(part) : true
          )
          if (required.length === 0) continue
          if (!required.some((part) => this.optionValueMatches(selected, part))) return false
        }
        return true
      }

      /** Show only the slides matching the resolved pair variant, like the PDP gallery does. */
      filterGallery(variant) {
        if (!this.gallery || !variant) return
        if (!this.allPairOptionValues) {
          this.allPairOptionValues = new Set(
            this.variants.flatMap((v) => [v.o1, v.o2, v.o3]).filter(Boolean).map((o) => this.normalize(o))
          )
        }
        const options = [variant.o1, variant.o2, variant.o3].filter(Boolean).map((o) => this.normalize(o))
        const slides = Array.from(this.gallery.querySelectorAll('.bundle-upsell-modal__slide'))
        let visible = 0
        slides.forEach((slide) => {
          const show = this.variantCheck(options, slide.dataset.mediaAlt || '')
          slide.style.display = show ? '' : 'none'
          if (show) visible++
        })
        if (visible === 0) slides.forEach((slide) => (slide.style.display = ''))
      }

      /** Snap the modal gallery to the resolved variant's featured media (or the first visible slide). */
      scrollGalleryToVariant(variant, instant) {
        if (!this.gallery || !variant) return
        this.filterGallery(variant)
        let slide = variant.mid != null ? this.gallery.querySelector(`[data-media-id="${variant.mid}"]`) : null
        if (!slide || slide.style.display === 'none') {
          // Filtering changed the layout under the current scroll position — reset to the first visible slide
          slide = Array.from(this.gallery.querySelectorAll('.bundle-upsell-modal__slide')).find(
            (s) => s.style.display !== 'none'
          )
        }
        if (!slide) return
        const left =
          slide.offsetLeft - this.gallery.offsetLeft - (this.gallery.clientWidth - slide.clientWidth) / 2
        this.gallery.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' })
      }

      onModalChange(evt) {
        if (evt.target.matches('[data-bundle-modal-option] input')) this.updateModalState()
      }

      updateModalState() {
        if (!this.modal) return
        const selections = this.getSelections()

        // Mark values that have no available variant for the current metal + other selections.
        this.optionFieldsets.forEach((fieldset) => {
          fieldset.querySelectorAll('input').forEach((radio) => {
            const test = { ...selections, [fieldset.dataset.optionPosition]: radio.value }
            const v = this.variantForSelections(test)
            const pill = radio.closest('.bundle-upsell-modal__pill')
            pill.classList.toggle('is-unavailable', !v || !v.a)
            pill.classList.toggle('is-selected', radio.checked)
          })
        })

        const variant = this.variantForSelections(selections)
        if (this.modalImage && variant && variant.i) this.modalImage.src = variant.i
        if (this.modalMatchEl && variant) this.modalMatchEl.textContent = this.pairVariantTitle(variant)
        this.scrollGalleryToVariant(variant)
        const totals = variant ? this.setTotal(variant.p, variant.cap) : null
        if (this.modalPrice) this.modalPrice.textContent = totals ? this.formatMoney(totals.total) : ''
        if (this.modalCompare) {
          this.modalCompare.hidden = !totals || totals.savings === 0
          if (totals && totals.savings > 0) this.modalCompare.textContent = this.formatMoney(totals.compareTotal)
        }

        const available = !!(variant && variant.a)
        if (this.confirmBtn) {
          if (available) this.confirmBtn.removeAttribute('disabled')
          else this.confirmBtn.setAttribute('disabled', 'disabled')
        }
        if (this.confirmText) {
          this.confirmText.textContent = available
            ? this.dataset.buttonLabel || ''
            : this.dataset.soldOutLabel || ''
        }
        this.modalVariantId = available ? variant.id : null
      }

      onConfirmClick(evt) {
        evt.preventDefault()
        if (!this.modalVariantId || this.confirmBtn.hasAttribute('disabled')) return
        this.addToCart(this.modalVariantId, this.confirmBtn, () => this.closeModal())
      }

      /* ========== Add to cart ========== */

      onAddClick(evt) {
        evt.preventDefault()
        if (!this.button || this.button.hasAttribute('disabled')) return
        if (this.multiOption) {
          this.openModal()
          return
        }
        const id = parseInt(this.dataset.variantId, 10)
        if (!id || isNaN(id)) return
        this.addToCart(id, this.button)
      }

      /** The main product line item exactly as the primary ATC would add it (minus file uploads). */
      getMainItem() {
        const form = document.getElementById(`product-form-${this.dataset.section}`)
        if (!form) return null
        const id = parseInt(form.querySelector('[name="id"]')?.value, 10)
        if (!id || isNaN(id)) return null
        const sectionEl = this.closest('section[data-section]')

        const validation = CartAdd.validateInputModal(sectionEl, form)
        if (!validation.valid) {
          validation.inputModal.openAndShowErrors(validation.invalidFields)
          return 'invalid'
        }

        const formData = new FormData(form)
        const item = { id, quantity: parseInt(formData.get('quantity'), 10) || 1 }
        const properties = CartAdd.buildMainProperties(formData, {
          section: sectionEl,
          skipFiles: true,
          skipEngravingWhenNo: true,
          skipUpsellGroupProps: true,
        })
        if (Object.keys(properties).length) item.properties = properties
        return { item, extras: CartAdd.getUpsellItems(sectionEl) }
      }

      addToCart(id, button, onSuccess) {
        if (!window.CartAdd || button.classList.contains('loading')) return
        this.hideError()

        // "Add to set" = main product + its selected upsells + the pair product
        const main = this.getMainItem()
        if (main === 'invalid') return
        const items = []
        if (main) {
          items.push(main.item)
          main.extras.forEach((extra) => items.push(extra))
        }
        items.push({ id: parseInt(id, 10), quantity: 1 })

        const cart = CartAdd.getCart()
        const { sections, sectionsUrl } = CartAdd.getSectionsConfig(cart)
        CartAdd.setButtonLoading(button, button.parentElement, true)

        CartAdd.postJSON(items, sections, sectionsUrl)
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message)
              return
            }
            if (onSuccess) onSuccess(response)
            if (!cart) {
              window.location = routes.cart_url
              return
            }
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-upsell',
              productVariantId: parseInt(id, 10),
              cartData: response,
            })
            cart.renderContents(response)
          })
          .catch(() => {
            this.showError(window.cartStrings ? window.cartStrings.error : '')
          })
          .finally(() => {
            CartAdd.setButtonLoading(button, button.parentElement, false)
          })
      }
    }
  )
}
